import React, { useMemo, useState } from 'react'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { PurchaseProps } from '@/entities/purchase'
import { format, isToday, isYesterday, isThisWeek, isThisMonth, isThisYear } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Button } from '../ui/button'
import { RefreshCcw } from 'lucide-react'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

interface PurchasesChartProps {
  data: PurchaseProps[]
  title?: string
}

const statusOptions = [
  { label: 'Aguardando', value: 'PENDING' },
  { label: 'Pago', value: 'PAID' },
  { label: 'Falha', value: 'FAILED' },
  { label: 'Devolvido', value: 'REFUNDED' }
]

const periodOptions = [
  { label: 'Hoje', value: 'today' },
  { label: 'Ontem', value: 'yesterday' },
  { label: 'Essa semana', value: 'thisWeek' },
  { label: 'Esse mês', value: 'thisMonth' },
  { label: 'Esse ano', value: 'thisYear' },
  { label: 'Todos', value: 'all' }
]

const PurchasesChart: React.FC<PurchasesChartProps> = ({ data, title = 'Compras', isLoading = false }) => {
  const [dateFilter, setDateFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('PAID')

  const filteredData = useMemo(() => {
    return data
      .filter(purchase => purchase.status === statusFilter)
      .filter(purchase => {
        const date = new Date(purchase.createdAt)
        switch (dateFilter) {
          case 'today':
            return isToday(date)
          case 'yesterday':
            return isYesterday(date)
          case 'thisWeek':
            return isThisWeek(date, { weekStartsOn: 1 })
          case 'thisMonth':
            return isThisMonth(date)
          case 'thisYear':
            return isThisYear(date)
          default:
            return true
        }
      })
  }, [data, dateFilter, statusFilter])

  const groupedData = useMemo(() => {
    const result: Record<string, number> = {}

    for (const purchase of filteredData) {
      const dateKey = format(new Date(purchase.createdAt), 'dd/MM/yyyy', { locale: ptBR })
      result[dateKey] = (result[dateKey] || 0) + purchase.value
    }

    return result
  }, [filteredData])

  const chartLabels = Object.keys(groupedData)
  const chartValues = Object.values(groupedData)

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: 'Valor (R$)',
        data: chartValues,
        backgroundColor: 'rgb(255, 105, 180)',
        borderRadius: 4
      }
    ]
  }

  const handleClick = (event: MouseEvent, elements: any[]) => {
    if (elements.length > 0) {
      const index = elements[0].index
      const clickedDate = chartLabels[index]
      setDateFilter(clickedDate)
    }
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false }
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        ticks: { precision: 0 },
        grid: { color: 'rgba(0, 0, 0, 0.05)' }
      }
    },
    onClick: handleClick,
    animation: { duration: 2000 }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 h-[460px] flex flex-col gap-4 transition-all duration-200 hover:shadow-md">
      <div className="flex w-full justify-between items-center">
        <div className="flex-1 flex-row gap-2 items-center justify-start">
          <span className="text-sm font-thin text-muted-foreground">Status</span>
          <select className="border rounded-lg px-2 py-1 text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <span className="text-md font-semibold text-muted-foreground w-full text-center">{title}</span>
        <div className="flex-1 flex-row gap-2 items-center justify-end">
          <span className="text-sm font-thin text-muted-foreground">Período</span>
          <select className="border rounded-lg px-2 py-1 text-sm" value={dateFilter} onChange={e => setDateFilter(e.target.value)}>
            {periodOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {chartData.labels.length > 0 ? (
        <div className="flex-1">
          <Bar data={chartData} options={options as any} />
        </div>
      ) : (
        <div className="flex flex-col justify-center items-center gap-5 w-full h-full">
          <p className="text-md text-gray-600 text-muted-foreground">Nenhum dado encontrado!</p>
          <Button variant="outline" size="sm" onClick={() => setDateFilter('all')}>
            <RefreshCcw className="w-4 h-4 mr-2" /> Resetar filtros
          </Button>
        </div>
      )}
    </div>
  )
}

export default PurchasesChart
